import { Button, Link, Typography, alpha } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanManageCatalogItems } from 'entities/User/model/selectors/userPermissionsSeletors';
import { addInventoryItemActions } from 'features/AddInventoryItem/model/slices/AddInventoryItemSlice';
import { useMarketplaceNavigation } from 'features/SearchCatalogItems/lib/hooks/useMarketplaceNavigation';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import openBoxSearch from 'shared/assets/svg/open-box-search.svg';
import { getRouteCatalogInventoryCategories } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { EmptyPlaceholder } from 'shared/ui/EmptyPlaceholder/EmptyPlaceholder';
import { EmptyResults } from 'shared/ui/EmptyResults';

interface InventoryTableEmptyProps {
    isActiveSearch: boolean;
    isOrdersAddItemsFlow?: boolean;
}

export const InventoryTableEmpty = ({
    isActiveSearch,
    isOrdersAddItemsFlow = false,
}: InventoryTableEmptyProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const canEditCatalog = useSelector(getUserCanManageCatalogItems);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const propertyId = selectedPropertyId || params.propertyId;

    const { navigateToMarketplace } = useMarketplaceNavigation(
        'property-catalog',
        isOrdersAddItemsFlow,
    );

    const onAddItems = useCallback(() => {
        if (!propertyId) {
            return;
        }

        dispatch(searchCatalogItemsActions.resetSearch());
        dispatch(addInventoryItemActions.setEditItemMode(true));
        navigate(getRouteCatalogInventoryCategories(propertyId));
        dispatch(catalogItemActions.closeDetailedItem());
        dispatch(categoryActions.clearCategorySelection());
    }, [dispatch, navigate, propertyId]);

    if (isActiveSearch) {
        return (
            <EmptyPlaceholder
                image={openBoxSearch}
                childrenContainerSx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography
                    color={(theme) => alpha(theme.palette.common.black, 0.5)}
                    variant="subtitle2"
                    sx={{
                        maxWidth: '280px',
                        textAlign: 'center',
                        marginBottom: '16px',
                    }}
                >
                    {t('No items found in the inventory list')}.
                </Typography>
                {canEditCatalog && (
                    <Link
                        data-testid="search-property-catalog-link"
                        component="button"
                        variant="subtitle2"
                        sx={{
                            lineHeight: '2.4rem',
                            textAlign: 'center',
                            cursor: 'pointer',
                        }}
                        underline="hover"
                        onClick={() => navigateToMarketplace()}
                    >
                        {t('Search the Property Catalog')}.
                    </Link>
                )}
            </EmptyPlaceholder>
        );
    }

    return (
        <EmptyResults
            title={t('No items in inventory')}
            description={t(
                'Track the parts and supplies you keep on hand. ' +
                    'Arrange items to match your shelves, making it easy ' +
                    'to count inventory and reorder stock.',
            )}
            dataTestClass="inventory-table-empty"
            action={
                canEditCatalog ? (
                    <Button
                        data-testid="btn-add-inventory-items-empty"
                        variant="contained"
                        onClick={onAddItems}
                    >
                        {t('Add items')}
                    </Button>
                ) : undefined
            }
        />
    );
};
